import type { DenyRxChangeRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  DEFAULT_RX_CHANGE_DENIAL_REASON,
  RX_CHANGE_DENIAL_REASON_OPTIONS,
  useDenyRxChangeMutation,
} from '@/entities/rx';
import { FormField } from '@/shared/ui/primitives';

import { nullIfBlank } from '../lib';
import { rxChangeDenyDraftSchema, type RxChangeDenyDraft } from '../model/schemas';
import { useDetailAction } from '../model/useDetailAction';
import { DetailActionForm } from './DetailActionForm';

export interface RxChangeDenyFormProps {
  readonly rxChangeId: number;
  /** Called after a successful deny (e.g. to close the parent sheet). */
  readonly onCompleted?: () => void;
}

const DEFAULT_VALUES: RxChangeDenyDraft = {
  reason: DEFAULT_RX_CHANGE_DENIAL_REASON,
  comment: '',
};

/**
 * Deny a pending RxChange with a required reason and optional comment.
 * Mutation success invalidates ``rxKeys.pendingRxChangesDetailed()`` (prefix match).
 */
export function RxChangeDenyForm({ rxChangeId, onCompleted }: RxChangeDenyFormProps) {
  const mutation = useDenyRxChangeMutation();
  const { form, handleSubmit } = useDetailAction({
    schema: rxChangeDenyDraftSchema,
    defaultValues: DEFAULT_VALUES,
    mutation,
    buildVariables: (draft: RxChangeDenyDraft) => ({
      rxChangeId,
      data: {
        Reason: draft.reason,
        Comment: nullIfBlank(draft.comment),
      } satisfies DenyRxChangeRequestT,
    }),
    successMessage: 'RxChange denied.',
    errorTitle: 'Could not deny RxChange.',
    logKey: 'deny RxChange',
    logContext: { rxChangeId },
    onCompleted,
  });

  return (
    <DetailActionForm
      mutation={mutation}
      onSubmit={handleSubmit}
      submitLabel="Deny RxChange"
      pendingLabel="Denying…"
      submitVariant="destructive"
    >
      <FormField
        form={form}
        type="select"
        name="reason"
        label="Denial reason"
        options={[...RX_CHANGE_DENIAL_REASON_OPTIONS]}
        placeholder="Select a denial reason"
      />
      <FormField
        form={form}
        type="textarea"
        name="comment"
        label="Comment"
        placeholder="Optional note for the pharmacy"
      />
    </DetailActionForm>
  );
}
